import { define, html, router, store } from "hybrids";

import layout from "~/factories/layout";

import ArtistVideo, { removeComment, toggleCommentLike, } from "~/stores/ArtistVideo";
import Comment from "~/stores/Comment";
import Session from "~/stores/Session";

import { VBroadcastContainer } from "~/modules/broadcast/elements/Container";
import { VBroadcastChatForm } from "~/modules/broadcast/elements/ChatForm";
import { CommentClickEvent } from "~/modules/broadcast/elements/ChatComment";

import Share from "./Share";
import User from "./User";
import Donate from "./donate/Root";

interface VVideoChatView extends HTMLElement {
  video?: ArtistVideo;
  comments: Comment[];
  session: Session;
  limit: number;
}

const DEFAULT_LIMIT = 20;

function offsetLimit(host: VVideoChatView) {
  host.limit += DEFAULT_LIMIT;

  const container = host.querySelector(
    "v-broadcast-container[stick-to-bottom]"
  ) as VBroadcastContainer;
  if (container) {
    const firstComment = container.children[1];

    requestAnimationFrame(() => {
      firstComment.scrollIntoView({ block: "center" });
    });
  }
}

function setCommentQuote(host: VVideoChatView, event: CommentClickEvent) {
  const form = host.querySelector("v-broadcast-chat-form") as VBroadcastChatForm;
  form.quote = event.detail.comment;
  form.input.focus();
}

function remove(host: VVideoChatView, event: CommentClickEvent) {
  if (confirm("Are you sure you want to delete this comment?")) {
    removeComment(host.video as ArtistVideo, event.detail.comment);
  }
}

function toggleLike(host: VVideoChatView, event: CommentClickEvent) {
  toggleCommentLike(host.video as ArtistVideo, event.detail.comment);
}

function focusForm(host: VVideoChatView) {
  const form = host.querySelector("v-broadcast-chat-form") as VBroadcastChatForm;
  if (form) form.input.focus();
}

export default define<VVideoChatView>({
  [router.connect]: { url: "/watch/:video/chat", stack: [Donate, User, Share] },
  tag: "v-video-chat-view",
  video: store(ArtistVideo),
  session: store(Session),
  comments: ({ video, limit }, lastComments) => {
    if (!video || !store.ready(video)) return [];

    const offset = lastComments
      ? video.comments.findIndex((c) => lastComments.includes(c))
      : 0;

    return video.comments.slice(0, limit + offset).reverse();
  },
  limit: DEFAULT_LIMIT,
  render: layout("flex"),
  content: ({ video, comments, session, limit }) => html`
    ${video &&
    store.ready(video) &&
    html`
      ${!!comments.length
        ? html`
          <v-broadcast-container overflow grow stick-to-bottom>
            ${video.comments.length > limit &&
            html`
              <v-ui-button-group>
                <v-ui-button variant="inline" onclick="${offsetLimit}">
                  <v-ui-text color="text-secondary">
                    <v-ui-icon
                      name="chat"
                      size="small"
                      color="text-secondary"
                    ></v-ui-icon>
                    Show older comments
                  </v-ui-text>
                </v-ui-button>
              </v-ui-button-group>`}
            ${comments
              .filter(
                ({ user }) =>
                  store.error(session) ||
                  (store.ready(session) && !session.blockedUsers.includes(user))
              )
              .map((comment, index) =>
                html`
                  <v-broadcast-chat-comment
                    comment="${comment}"
                    context="${video}"
                    href="${router.url(User, { user: comment.user })}"
                    onreplay="${setCommentQuote}"
                    onremove="${remove}"
                    ontogglelike="${toggleLike}"
                    transition="${limit > DEFAULT_LIMIT &&
                    index < DEFAULT_LIMIT}"
                  ></v-broadcast-chat-comment>
                `.key(comment.id)
              )}
          </v-broadcast-container>`
        : html`
          <v-broadcast-placeholder icon="chat" onclick=${focusForm}>
            Start conversation here...
          </v-broadcast-placeholder>
        `}
      <v-broadcast-chat-form
        context="${video}"
        context-type="video"
      ></v-broadcast-chat-form>
    `}
  `,
});
